import * as React from 'react';
import * as Yup from 'yup';
import { Mutation } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
import { ErrorMessage, Field, FieldProps, Formik, FormikProps } from 'formik';
import { Span, Link, P } from '../../typography';
import { AuthenticateUser } from '../../graphql';
import { Box, Button, StyledForm, StyledInput } from '../../components';
import { JWT_LOCAL_STORAGE_KEY } from '../../consts/index';
import { getLoggedInUser } from '../../utils/helpers';
import { SteynCityContext } from '../../components/ContextProvider';
import { UserRole } from '../../generated/graphql';
import { theme } from '../../theme';
import packageJson from '../../../package.json';

const LoginValidationSchema = Yup.object().shape({
  email: Yup.string().email().required('Required'),
  password: Yup.string().min(6, 'Please enter a password at least 6 digits long.').required('Required'),
});

type LoginFormValues = {
  email: string;
  password: string;
};

type LoginFormProps = RouteComponentProps & {
  initialValues?: object;
  isVendor: boolean;
};

type LoginFormState = {
  showPassword: boolean;
  isSettingUser: boolean;
};

class LoginForm extends React.Component<LoginFormProps, LoginFormState> {
  static contextType = SteynCityContext;
  context!: React.ContextType<typeof SteynCityContext>;
  constructor(props: LoginFormProps) {
    super(props);
    this.state = {
      showPassword: false,
      isSettingUser: false,
    } as LoginFormState;
    this.handleRedirect = this.handleRedirect.bind(this);
  }

  handleRedirect = async (token: string) => {
    window.localStorage.setItem(JWT_LOCAL_STORAGE_KEY, token);
    const context = this.context;
    const user = getLoggedInUser();
    await context.setUser(user);
    if (user) {
      switch (user.role) {
        case UserRole.EventManager:
          this.props.history.push('/events/all');
          break;
        case UserRole.EventAdmin:
          this.props.history.push('/events/all');
          break;
        case UserRole.VendorAdmin:
          this.props.history.push('/transactions');
          break;
        case UserRole.VendorUser:
          this.props.history.push('/transactions');
          break;
        case UserRole.SecurityAdmin:
          this.props.history.push('/security-management');
          break;
        case UserRole.StakeholderAdmin:
          this.props.history.push('/stakeholders/all');
          break;
        case UserRole.ContentAdmin:
          this.props.history.push('/content-management/notifications');
          break;
        case UserRole.ClubAdmin:
          this.props.history.push('/clubs');
          break;
        case UserRole.SalesAgent:
          this.props.history.push('/property-listings');
          break;
        default:
          this.props.history.push('/dashboard');
          break;
      }
    }
  };

  onClick = () => {
    this.props.history.push('/forgot-password');
  };

  onCreateAccountClick = () => {
    const { history } = this.props;
    history.push('/createVendorAccount');
  };

  render() {
    const { showPassword } = this.state;
    const { isVendor } = this.props;
    return (
      <Mutation onCompleted={(data) => this.handleRedirect(data.authenticateUser.token)} mutation={AuthenticateUser}>
        {(authUser, { loading, error }) => {
          return (
            <Formik
              validationSchema={LoginValidationSchema}
              initialValues={{ email: '', password: '' }}
              onSubmit={(values: LoginFormValues) =>
                authUser({
                  variables: { email: values.email.toLowerCase(), password: values.password },
                })
              }
              render={(formikProps: FormikProps<LoginFormValues>) => {
                return (
                  <StyledForm data-testid="login-form">
                    <Field
                      name="email"
                      render={({ field }: FieldProps<LoginFormValues>) => (
                        <Box width="100%" mb={3}>
                          <StyledInput type="email" px={2} {...field} placeholder="Enter email" />
                          <ErrorMessage name="email">
                            {(msg) => (
                              <Span mt={2} color="error" textAlign="right">
                                {msg}
                              </Span>
                            )}
                          </ErrorMessage>
                        </Box>
                      )}
                    />
                    <Field
                      name="password"
                      render={({ field }: FieldProps<LoginFormValues>) => (
                        <Box width="100%" mb={3}>
                          <StyledInput
                            px={2}
                            {...field}
                            showEyeIcon={true}
                            placeholder="Password"
                            passwordVisible={showPassword}
                            type={showPassword ? 'text' : 'password'}
                            onTogglePassword={() =>
                              this.setState({
                                showPassword: !this.state.showPassword,
                              })
                            }
                          />
                          <ErrorMessage name="password">
                            {(msg) => (
                              <Span mt={2} color="error" textAlign="right">
                                {msg}
                              </Span>
                            )}
                          </ErrorMessage>
                        </Box>
                      )}
                    />
                    {error && (
                      <Span width="100%" textAlign="center" color="error" mb={3}>
                        {error.message.replace('GraphQL error:', '').trim()}
                      </Span>
                    )}
                    <Button type="submit" text="SUBMIT" isLoading={loading} disabled={loading} />
                    <Box width="100%" justifyContent="space-between" pt={1} flexDirection="row" alignItems="center">
                      <P color="transparentBlack">v{packageJson.version}</P>
                      <Link color="transparentBlack" pointer={true} onClick={this.onClick}>
                        Forgot Password?
                      </Link>
                    </Box>
                    {isVendor && (
                      <Box width="100%" flexDirection="row" pt={1} ml={1} alignItems="center">
                        <p style={{ color: theme.colors.transparentBlack, marginRight: 2, fontSize: 12 }}>
                          Don't have an account ?
                        </p>
                        <Link
                          style={{ textDecoration: 'underline', fontSize: 12 }}
                          pointer={true}
                          fontWeight={700}
                          onClick={this.onCreateAccountClick}
                          color={theme.colors.putty}
                        >
                          Create one here
                        </Link>
                      </Box>
                    )}
                  </StyledForm>
                );
              }}
            />
          );
        }}
      </Mutation>
    );
  }
}

export default withRouter(LoginForm);



// WEBPACK FOOTER //
// ./src/forms/login/LoginForm.tsx